<template>
  <div
    v-if="!readonly"
    class="cfs-field text-field d-flex flex-column"
    :class="{ 'static-bg': staticBg, 'warning-messages': warningMessages?.length }"
  >
    <p v-if="label" class="input-title px-1 pb-1" :class="{ disabled: disabled }">
      {{ sharp ? `${label} #` : label }}
      <span v-if="optional" class="text-blue-havelock">(Optional)</span>
    </p>
    <v-text-field
      v-bind="{ ...$attrs, ...$props }"
      ref="inputRef"
      v-mask="format"
      :messages="warningMessages"
      :tabindex="tabindex"
      dense
      :value="formattedValue"
      outlined
      :placeholder="placeholder || label"
      :label="null"
      v-on="$listeners"
    >
      <template v-if="isLoading" #append>
        <v-progress-circular :size="20" color="primary" indeterminate />
      </template>
      <template v-if="items.length" #[dropdownDirection]>
        <CfsDropdown
          :tabindex="tabindex"
          :title="activeDropdown"
          :items="items"
          btn-class="cfs-field__button"
          icon-color="#333333"
          small
          @click-item="onClickItemHandler"
        />
      </template>
      <template v-for="(_, name) in $slots" #[name]>
        <slot :name="name" />
      </template>
    </v-text-field>
  </div>
  <CfsReadonlyField v-else v-bind="$props" :data="computedReadonlyValue" :loading="loading">
    <template #readonly-append>
      <slot name="readonly-append" />
    </template>
  </CfsReadonlyField>
</template>

<script>
import { useCurrencyInput } from 'vue-currency-input';
import { computed, watch } from 'vue';
import { GeneralVolumeUnits, GeneralWeightUnits } from '@/constants/measurementUnits';

export default {
  components: {
    CfsReadonlyField: () => import('./special/CfsReadonlyFieldComponent.vue'),
  },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    tabindex: {
      type: [Number, String],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    sharp: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    optional: {
      type: Boolean,
      default: false,
    },
    /**when set to false fills input with application background solid color instead of inherited one.
     * I.e. removes transparency from input*/
    staticBg: {
      type: Boolean,
      default: true,
    },
    warningMessages: {
      type: Array,
      default: null,
    },
    items: {
      type: Array,
      default: () => [],
    },
    activeItem: {
      type: [Number, String],
      default: '',
    },
    format: {
      type: [String, Array],
      default: '',
    },
    direction: {
      type: String,
      default: 'right',
      validate: (value) => ['left', 'right'].includes(value),
    },
    currencyOptions: {
      type: Object,
      required: true,
    },
    value: {
      type: [String, Number],
      default: '',
    },
  },
  emits: ['input', 'click-item'],
  setup(props, { emit }) {
    const computedIsActiveLbs = computed(
      () =>
        props.activeItem === GeneralWeightUnits.LBS || props.activeItem === GeneralVolumeUnits.CFT
    );

    const { inputRef, formattedValue, setValue, setOptions } = useCurrencyInput({
      ...props.currencyOptions,
      ...(computedIsActiveLbs.value ? { precision: 2 } : {}),
      locale: 'en',
    });

    watch(
      () => props.currencyOptions,
      (options) => {
        setOptions({
          ...options,
          ...(computedIsActiveLbs.value ? { precision: 2 } : {}),
        });
      }
    );

    watch(
      () => props.value,
      (value) => {
        setValue(value);
      }
    );

    const computedReadonlyValue = computed(() =>
      props.activeItem.length ? `${formattedValue.value} ${props.activeItem}` : formattedValue.value
    );

    return {
      inputRef,
      formattedValue,
      computedReadonlyValue,
    };
  },
  data() {
    return {
      activeDropdown: this.activeItem,
    };
  },
  computed: {
    dropdownDirection() {
      return this.direction === 'right' ? 'append' : 'prepend-inner';
    },
  },
  watch: {
    activeItem(value) {
      this.activeDropdown = value;
    },
  },
  methods: {
    onClickItemHandler(item, index) {
      this.activeDropdown = item.title;
      this.$emit('click-item', item, index);
    },
  },
};
</script>

<style lang="scss">
.warning-messages {
  .v-text-field {
    &__details {
      .v-messages {
        color: #e60909;
        font-weight: 600;
      }
    }
  }
}

.v-text-field__prefix {
  color: var(--v-text-base) !important;
  padding-right: 1px !important;
}
</style>
